import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';

import { IngestroImporterProps } from '@epi-models/pages/Beneficiaries';
import { getApplicationLanguageId } from '@epi-store/application/application.selectors';
import { beneficiariesImportActions } from '@epi-store/beneficiariesImport/beneficiariesImport.slice';
import {
  useImportUserDataMutation,
  useLazyGetImportStatusQuery,
  useValidateUserDataMutation
} from '@epi-api/massUpload/massUpload';
import {
  ImportUserDataPayload,
  ValidateUserDataPayload,
  ValidateUserDataResponse
} from '@epi-models/pages/MassUpload';
import {
  applyCellErrorsFromIssues,
  applyResultRowDefaults,
  applyReviewRowDefaults,
  getBenefitGroupPreselectionUpdates,
  getLocalIssues,
  getValidationErrorsFromResponse,
  getValidationErrorsFromUnknown,
  hasAnyUserProvidedImportData,
  getImportCellNullableStringValue,
  getImportCellStringValue,
  getImportCellValue,
  isEmptyImportCellValue,
  isImportDateInPast
} from '@epi-pages/Beneficiaries/helpers/ingestroValidationHelpers';
import { goToPage } from '@epi-actions/navigation';
import { routerPaths } from '@epi-constants/routerPaths';
import config from '@epi-config';
import { defaultDateFormat } from '@epi-constants/dateFormats';

import { fi } from './finnishIngestro';
import { format } from 'date-fns';

const { ingestroDeveloperMode } = config;

export const useIngestroImporter = ({
  groups,
  setUploadInProgress,
  requestId,
  setRequestId,
  allowNextNavigation
}: Omit<IngestroImporterProps, 'uploadInProgress'>) => {
  const dispatch = useDispatch();
  const { primaryFrontColor, gray0, gray100, sizeMedium, sizeExtraExtraLarge } =
    useTheme();
  const language = useSelector(getApplicationLanguageId);
  const { t } = useTranslation();
  const [validateUserData] = useValidateUserDataMutation();
  const [importUserData, { isLoading: isImporting }] =
    useImportUserDataMutation();
  const [getImportStatus] = useLazyGetImportStatusQuery();
  const languageEn = language === 'en';
  const customLanguage = languageEn ? 'en' : 'de';
  const defaultBenefitGroup = groups.length === 1 ? groups[0].value : undefined;
  const benefitGroupOptions = useMemo(() => {
    if (defaultBenefitGroup === undefined) {
      return groups;
    }

    const group = groups[0];

    return [
      {
        ...group,
        alternativeMatches: [
          ...(group.alternativeMatches ?? []),
          String(group.value),
          group.label
        ].filter(
          (match, index, matches) =>
            Boolean(match) && matches.indexOf(match) === index
        )
      }
    ];
  }, [defaultBenefitGroup, groups]);

  const asyncGetImportStatus = async () => {
    const importStatusResponse = await getImportStatus({
      requestId: requestId ?? undefined
    }).unwrap();
    setUploadInProgress(
      isImporting || importStatusResponse.status === 'Created'
    );
    dispatch(beneficiariesImportActions.setImportStatus(importStatusResponse));
    if (importStatusResponse.status === 'Processed') {
      allowNextNavigation?.();
      dispatch(goToPage(routerPaths.beneficiaries));
    }
    if (importStatusResponse.status === 'Failed') {
      allowNextNavigation?.();
      dispatch(goToPage(`${routerPaths.massUploadErrors}/${requestId}`));
    }
  };

  const blockImportError = useCallback(
    (
      block: (params: {
        title: string;
        description: string;
        closeButton: string;
      }) => void,
      errorType: 'empty' | 'service'
    ) => {
      block({
        title: t(
          `containers.beneficiaries.import_employees.${errorType}_error.title`
        ),
        description: t(
          `containers.beneficiaries.import_employees.${errorType}_error.description`
        ),
        closeButton: t(
          `containers.beneficiaries.import_employees.${errorType}_error.button`
        )
      });
    },
    [t]
  );
  const hasAnyUserProvidedData = useCallback(
    (rows: unknown[]) =>
      hasAnyUserProvidedImportData(rows, defaultBenefitGroup),
    [defaultBenefitGroup]
  );

  const settings = useMemo(
    () => ({
      modal: false,
      identifier: 'UAT User mass upload',
      developerMode: ingestroDeveloperMode,
      allowManualInput: true,
      enableExamples: false,
      automaticHeaderDetection: true,
      allowCustomColumns: false,
      onlyMappedColumns: false,
      disableExcelTemplate: false,
      disableTemplates: true,
      automaticMapping: false,
      multipleFileUpload: false,
      smartTable: true,
      cleaningAssistant: false,
      completeImportAction: 'block',
      successModalConfiguration: {
        display: false
      },
      language: customLanguage,
      submitModalConfiguration: {
        exportButton: false
      },
      i18nOverrides: {
        de: fi,
        en: {
          txt_page_upload_desc: t(
            'containers.beneficiaries.import_employees.txt_page_upload_desc'
          )
        }
      },
      inputTypes: ['csv', 'xlsx', 'xls'],
      contextualEngine: 'disabled',
      columnMappingConfiguration: {
        threshold: 0.6,
        layers: ['exact', 'historic', 'smart', 'fuzzy'],
        processingMode: 'node'
      },
      prompts: false,
      transpose: false,
      mappingViewMode: 'sourceColumns',
      ...(defaultBenefitGroup !== undefined
        ? {
            skipConfiguration: {
              mapping: {
                applyOptionMapping: 'always' as const,
                showInStepper: true,
                showInfoBox: false
              }
            }
          }
        : {}),
      columns: [
        {
          label: t(
            'containers.beneficiaries.import_employees.table.first_name.label'
          ),
          key: 'first_name',
          example: 'Mika',
          columnType: 'string',
          validations: [
            {
              validate: 'required',
              errorMessage: t(
                'containers.beneficiaries.import_employees.table.first_name.error'
              )
            }
          ]
        },
        {
          label: t(
            'containers.beneficiaries.import_employees.table.last_name.label'
          ),
          key: 'last_name',
          example: 'Ahosilta',
          columnType: 'string',
          validations: [
            {
              validate: 'required',
              errorMessage: t(
                'containers.beneficiaries.import_employees.table.last_name.error'
              )
            }
          ]
        },
        {
          label: t(
            'containers.beneficiaries.import_employees.table.personal_id.label'
          ),
          key: 'personal_id',
          description: t(
            'containers.beneficiaries.import_employees.table.personal_id.description'
          ),
          example: '11111977-123K',
          columnType: 'string',
          validations: [
            {
              validate: 'required',
              errorMessage: t(
                'containers.beneficiaries.import_employees.table.personal_id.error'
              )
            },
            {
              validate: 'unique',
              errorMessage: t(
                'containers.beneficiaries.import_employees.table.personal_id.error_unique'
              )
            }
          ]
        },
        {
          label: t(
            'containers.beneficiaries.import_employees.table.email_address.label'
          ),
          key: 'email_address',
          example: 'firstname.lastname@company.com',
          columnType: 'email',
          validations: [
            {
              validate: 'required',
              errorMessage: t(
                'containers.beneficiaries.import_employees.table.email_address.error'
              )
            }
          ]
        },
        {
          label: t(
            'containers.beneficiaries.import_employees.table.employee_number.label'
          ),
          key: 'employee_number',
          description: t(
            'containers.beneficiaries.import_employees.table.employee_number.description'
          ),
          example: 'A123',
          columnType: 'string'
        },
        {
          label: t(
            'containers.beneficiaries.import_employees.table.cost_center_number.label'
          ),
          key: 'cost_center_number',
          description: t(
            'containers.beneficiaries.import_employees.table.cost_center_number.description'
          ),
          example: '123A',
          columnType: 'string'
        },
        {
          label: t(
            'containers.beneficiaries.import_employees.table.employment_type.label'
          ),
          key: 'employment_type',
          columnType: 'category',
          description: t(
            'containers.beneficiaries.import_employees.table.employment_type.description'
          ),
          dropdownOptions: [
            {
              label: t(
                'containers.beneficiaries.import_employees.table.employment_type.options.full_time'
              ),
              alternativeMatches: ['Full-time', 'Kokopäiväinen'],
              type: 'string',
              value: 'full_time'
            },
            {
              label: t(
                'containers.beneficiaries.import_employees.table.employment_type.options.part_time'
              ),
              alternativeMatches: ['Part-time', 'Osa-aikainen'],
              type: 'string',
              value: 'part_time'
            },
            {
              label: t(
                'containers.beneficiaries.import_employees.table.employment_type.options.temporarily_employed'
              ),
              alternativeMatches: ['Temporary employed', 'Määräaikainen'],
              type: 'string',
              value: 'temporarily_employed'
            },
            {
              label: t(
                'containers.beneficiaries.import_employees.table.employment_type.options.on_hold'
              ),
              alternativeMatches: ['On hold', 'Poissa tilapäisesti'],
              type: 'string',
              value: 'on_hold'
            }
          ],
          optionMappingConfiguration: {
            layers: ['exact', 'fuzzy'],
            threshold: 0.6,
            processingMode: 'browser'
          }
        },
        {
          label: t(
            'containers.beneficiaries.import_employees.table.working_hours.label'
          ),
          key: 'working_hours_percentage',
          description: t(
            'containers.beneficiaries.import_employees.table.working_hours.description'
          ),
          example: '50',
          columnType: 'int',
          numberFormat: 'eu',
          validations: [
            {
              errorMessage: t(
                'containers.beneficiaries.import_employees.table.working_hours.error'
              ),
              validate: 'required_with_values',
              columnValues: {
                employment_type: ['part_time']
              }
            }
          ]
        },
        {
          label: t(
            'containers.beneficiaries.import_employees.table.lunch_benefit_days.label'
          ),
          key: 'lunch_benefit_monthly_days',
          description: t(
            'containers.beneficiaries.import_employees.table.lunch_benefit_days.description'
          ),
          example: '20',
          columnType: 'int',
          numberFormat: 'eu'
        },
        {
          label: t(
            'containers.beneficiaries.import_employees.table.entitled_from.label'
          ),
          key: 'benefits_start_date',
          description: t(
            'containers.beneficiaries.import_employees.table.entitled_from.description'
          ),
          example: '1.1.2026',
          columnType: 'date_dmy'
        },
        {
          label: t(
            'containers.beneficiaries.import_employees.table.employment_end.label'
          ),
          key: 'employment_end_date',
          description: t(
            'containers.beneficiaries.import_employees.table.employment_end.description'
          ),
          example: '1.1.2027',
          columnType: 'date_dmy'
        },
        {
          label: t(
            'containers.beneficiaries.import_employees.table.benefit_group.label'
          ),
          key: 'benefit_group',
          description: t(
            'containers.beneficiaries.import_employees.table.benefit_group.description'
          ),
          example:
            defaultBenefitGroup !== undefined
              ? String(defaultBenefitGroup)
              : 'Lunch',
          columnType: 'category',
          dropdownOptions: benefitGroupOptions,
          optionMappingConfiguration: {
            layers: ['exact', 'fuzzy'],
            threshold: defaultBenefitGroup !== undefined ? 0 : 0.6,
            processingMode: 'browser'
          },
          validations: [
            {
              validate: 'required',
              errorMessage: t(
                'containers.beneficiaries.import_employees.table.benefit_group.error'
              )
            }
          ]
        },
        {
          label: t(
            'containers.beneficiaries.import_employees.table.department.label'
          ),
          key: 'department',
          columnType: 'string'
        },
        {
          label: t(
            'containers.beneficiaries.import_employees.table.floor.label'
          ),
          key: 'floor',
          columnType: 'string'
        },
        {
          label: t(
            'containers.beneficiaries.import_employees.table.region.label'
          ),
          key: 'region',
          columnType: 'string'
        },
        {
          label: 'row_number',
          key: 'row_number',
          columnType: 'int',
          hidden: true,
          disabled: true
        }
      ],
      style: {
        globals: {
          primaryColor: primaryFrontColor,
          secondaryColor: gray0,
          primaryTextColor: gray100,
          secondaryTextColor: gray100,
          fontFamily: 'Montserrat'
        },
        buttons: {
          primary: {
            fontSize: sizeMedium,
            backgroundColor: primaryFrontColor,
            color: gray0,
            fontFamily: 'Montserrat',
            borderRadius: sizeExtraExtraLarge
          },
          secondary: {
            fontSize: sizeMedium,
            color: primaryFrontColor,
            backgroundColor: gray0,
            borderColor: primaryFrontColor,
            fontFamily: 'Montserrat',
            borderRadius: sizeExtraExtraLarge
          }
        }
      }
    }),
    [
      benefitGroupOptions,
      customLanguage,
      defaultBenefitGroup,
      gray0,
      gray100,
      primaryFrontColor,
      sizeExtraExtraLarge,
      sizeMedium,
      t
    ]
  );

  const stepHandler = useMemo(
    () => ({
      mappingStep: async ({ data, updateData }) => {
        if (defaultBenefitGroup === undefined) {
          return;
        }

        updateData(
          data.map(row => {
            let newRow = row;
            const benefitGroup = (row as Record<string, unknown>).benefit_group;

            if (isImportDateInPast(row.benefits_start_date)) {
              newRow = {
                ...newRow,
                benefits_start_date: format(new Date(), defaultDateFormat)
              };
            }

            if (isEmptyImportCellValue(getImportCellValue(benefitGroup))) {
              newRow = {
                ...newRow,
                benefit_group: defaultBenefitGroup
              };
            }

            return newRow;
          })
        );
      },
      reviewStep: async ({ data, updateData, block }) => {
        const invalidCellLabel = t(
          'containers.beneficiaries.import_employees.ingestro_invalid_cell'
        );
        const getErrorMessage = (columnKey: string, code: string) => {
          const key = `containers.beneficiaries.import_employees.errors.${columnKey}.${code}`;
          const translated = t(key);
          return translated === key ? undefined : translated;
        };
        const rows = data as Record<string, unknown>[];
        if (!hasAnyUserProvidedData(rows)) {
          blockImportError(block, 'empty');
          return;
        }

        const rowsWithRowNumbers = rows.map((row, rowIndex) =>
          applyReviewRowDefaults(row, rowIndex, defaultBenefitGroup)
        );

        const payload = rowsWithRowNumbers.map((row, rowIndex) => ({
          row_number: rowIndex + 1,
          personal_id: getImportCellStringValue(row.personal_id),
          benefits_start_date: getImportCellStringValue(
            row.benefits_start_date
          ),
          employment_end_date: getImportCellNullableStringValue(
            row.employment_end_date
          )
        }));

        const localIssues = getLocalIssues(
          rowsWithRowNumbers,
          invalidCellLabel
        );
        if (localIssues.length > 0) {
          updateData(
            applyCellErrorsFromIssues(
              rowsWithRowNumbers,
              localIssues,
              invalidCellLabel,
              getErrorMessage
            ) as Parameters<typeof updateData>[0]
          );
          return;
        }

        try {
          const validationResponse = (await validateUserData(
            payload as ValidateUserDataPayload
          ).unwrap()) as ValidateUserDataResponse;

          if (!validationResponse.isValid) {
            const issues = getValidationErrorsFromResponse(validationResponse);
            if (issues.length > 0) {
              updateData(
                applyCellErrorsFromIssues(
                  rowsWithRowNumbers,
                  issues,
                  invalidCellLabel,
                  getErrorMessage
                ) as Parameters<typeof updateData>[0]
              );
              return;
            }

            blockImportError(block, 'service');
            return;
          }

          updateData(rowsWithRowNumbers as Parameters<typeof updateData>[0]);
        } catch (validationError) {
          const issues = getValidationErrorsFromUnknown(validationError);
          if (issues.length > 0) {
            updateData(
              applyCellErrorsFromIssues(
                rowsWithRowNumbers,
                issues,
                invalidCellLabel,
                getErrorMessage
              ) as Parameters<typeof updateData>[0]
            );
            return;
          }

          blockImportError(block, 'service');
        }
      }
    }),
    [
      blockImportError,
      defaultBenefitGroup,
      hasAnyUserProvidedData,
      t,
      validateUserData
    ]
  );

  const handleResults = useCallback(
    async (result, _errors, complete, _logs, block) => {
      const rows = Array.isArray(result) ? result : [];
      if (!hasAnyUserProvidedData(rows)) {
        blockImportError(block, 'empty');
        return;
      }

      const rowsWithImportRowNumbers = rows.map((row, rowIndex) =>
        applyResultRowDefaults(
          row && typeof row === 'object'
            ? (row as Record<string, unknown>)
            : {},
          rowIndex,
          defaultBenefitGroup
        )
      ) as unknown as ImportUserDataPayload;

      try {
        const response = await importUserData(
          rowsWithImportRowNumbers
        ).unwrap();
        setRequestId(response.id);
        await complete();
      } catch {
        setUploadInProgress(false);
        blockImportError(block, 'service');
      }
    },
    [
      blockImportError,
      defaultBenefitGroup,
      getImportStatus,
      hasAnyUserProvidedData,
      importUserData
    ]
  );

  const onEntryChange = useCallback(
    (rows: Parameters<typeof getBenefitGroupPreselectionUpdates>[0]) =>
      getBenefitGroupPreselectionUpdates(rows, defaultBenefitGroup),
    [defaultBenefitGroup]
  );

  return {
    asyncGetImportStatus,
    settings,
    stepHandler,
    handleResults,
    onEntryChange
  };
};
